import { Injectable, Inject, forwardRef } from '@nestjs/common';
import { InjectModel } from '@nestjs/mongoose';
import { Model } from 'mongoose';
import { Types } from 'mongoose';
import { Request } from 'express';

import { Order, OrderDocument } from '../schemas/order.schema';
import { Product, ProductDocument } from '@/modules/product/schemas/product.schema';
import { CreateOrderDto } from '../dto/create-order.dto';
import { OrderStatus } from '../../../common/enums/order-status.enum';
import { OrderItemType, PaymentMethod, PaymentStatus } from '../enums/order-item-type.enum';
import { CouponType } from '../../coupon/schemas/coupon.schema';

// Circular dependencies
import { ProductService } from '../../product/services/product.service';
import { UserService } from '../../user/services/user.service';
import { CouponService } from '../../coupon/services/coupon.service';
import { AuthService } from '../../auth/services/auth.service';
import { InventoryService } from '../../inventory/services/inventory.service';
import { PackageService } from '../../package/services/package.service';
import { CustomerStatisticsService } from '../../statistics/services/customer-statistics.service';
import { EmailService } from '../../email/email.service';
import { BusinessProfileService } from '../../business-profile/services/business-profile.service';

@Injectable()
export class OrderService {
  constructor(
    @InjectModel(Order.name) private orderModel: Model<OrderDocument>,
    @Inject(forwardRef(() => ProductService))
    private readonly productService: ProductService,
    @Inject(forwardRef(() => UserService))
    private readonly userService: UserService,
    @Inject(forwardRef(() => CouponService))
    private readonly couponService: CouponService,
    @Inject(forwardRef(() => AuthService))
    private readonly authService: AuthService,
    @Inject(forwardRef(() => InventoryService))
    private readonly inventoryService: InventoryService,
    @Inject(forwardRef(() => PackageService))
    private readonly packageService: PackageService,
    @Inject(forwardRef(() => CustomerStatisticsService))
    private readonly customerStatisticsService: CustomerStatisticsService,
    private readonly emailService: EmailService,
    @Inject(forwardRef(() => BusinessProfileService))
    private readonly businessProfileService: BusinessProfileService
  ) {}

  private extractUserIdFromRequest(request: Request): string | null {
    const authHeader = request?.headers?.authorization;
    if (!authHeader) return null;
    
    const [type, token] = authHeader.split(' ');
    if (type !== 'Bearer' || !token) return null;
    
    try {
      const payload = this.authService.verifyToken(token);
      return payload?.sub || null;
    } catch (e) {
      return null;
    }
  }

  async create(createOrderDto: CreateOrderDto, request?: Request): Promise<OrderDocument> {
    try {
      // Set default values if not provided
      if (!createOrderDto.orderId) {
        createOrderDto.orderId = new Types.ObjectId().toString();
      }
      
      if (!createOrderDto.orderDate) {
        createOrderDto.orderDate = new Date();
      }

      createOrderDto.isDeposit = !!createOrderDto.isDeposit;
      
      // Extract user from token if not provided in DTO
      if (request?.headers?.authorization) {
        const userId = this.extractUserIdFromRequest(request);
        if (userId) {
          // Verify user exists
          const user = await this.userService.findById(userId);
          if (user) {
            createOrderDto.user = userId;
            // Set customerId if not provided
            if (!createOrderDto.customerId) {
              createOrderDto.customerId = userId;
            }
          }
        }
      }

      // Validate payment method is enabled
      if (createOrderDto.paymentMethod) {
        try {
          const settings = await this.businessProfileService.getPaymentMethodSettings();
          const paymentMethod = createOrderDto.paymentMethod;
          
          if (paymentMethod === PaymentMethod.CASH && !settings.cashEnabled) {
            throw new Error('Cash payment method is currently disabled');
          }
          if (paymentMethod === PaymentMethod.VODAFONE_CASH && !settings.walletEnabled) {
            throw new Error('Wallet payment method is currently disabled');
          }
          if (paymentMethod === PaymentMethod.PAYMOB && !settings.paymobEnabled) {
            throw new Error('Paymob payment method is currently disabled');
          }
        } catch (error) {
          // If business profile not found, allow order creation (backward compatibility)
          if (error.message.includes('Business profile not found')) {
          } else {
            throw error;
          }
        }
      }

      // Validate payment image requirement
      // if (createOrderDto.paymentMethod === PaymentMethod.VODAFONE_CASH || createOrderDto.isDeposit) {
      //   if (!createOrderDto.cashPayment?.paymentImage) {
      //     throw new Error('Payment proof image is required for Vodafone Cash payments or deposit orders');
      //   }
      // }

      // Validate items and calculate totals
      for (const item of createOrderDto.items) {
        
        try {
          if (item.itemType === OrderItemType.PACKAGE) {
            // Handle package validation
            const packageId = this.extractProductId(item);
            const packageData = await this.packageService.findOne(packageId);
            if (!packageData) {
              throw new Error(`Package with ID ${packageId} not found`);
            }
            
            // Check package stock
            if (packageData.stock < item.quantity) {
              throw new Error(`Insufficient stock for package ${packageData.name}. Available: ${packageData.stock}, Requested: ${item.quantity}`);
            }
            
      // Validate package items and their variants
      if (item.packageItems && item.packageItems.length > 0) {
        await this.validatePackageItems(packageData, item.packageItems);
        
        // Transform selectedVariants to ensure proper format
        item.packageItems = item.packageItems.map(packageItem => ({
          ...packageItem,
          selectedVariants: packageItem.selectedVariants?.map(variant => ({
            variant: variant.variant,
            value: this.ensureMultilingualValue(variant.value),
            image: variant.image,
            // _id: variant._id
          }))
        }));
      }
            
            // Set price if not provided
            if (!item.price) {
              const basePackagePrice = packageData.price ?? 0;
              const discountedPackagePrice =
                packageData.discountPrice != null && packageData.discountPrice < basePackagePrice
                  ? packageData.discountPrice
                  : undefined;

              item.price = basePackagePrice;
              if (discountedPackagePrice != null) {
                // If discountPrice is not provided in item, use product discountPrice as discount amount
                if (!item.discountPrice) {
                  item.discountPrice = basePackagePrice - discountedPackagePrice;
                }
              }
            }

            // Calculate final price: basePrice - discountPrice (treat discountPrice as discount amount)
            const finalPackagePrice =
              item.discountPrice != null && item.discountPrice >= 0
                ? Math.max(0, item.price - item.discountPrice)
                : item.price;

            // Keep discountPrice as the discount amount, don't overwrite it with final price
            item.totalPrice = finalPackagePrice * item.quantity;
          } else {
            // Handle product validation
            const productId = this.extractProductId(item);
            const product: ProductDocument = await this.productService.findOne(productId);
            if (!product) {
              throw new Error(`Product with ID ${productId} not found`);
            }
            
            
            // Check stock
            if (product.stock < item.quantity) {
              throw new Error(`Insufficient stock for product ${product.name}. Available: ${product.stock}, Requested: ${item.quantity}`);
            }
            
            const pricing = this.resolveProductPricing(product, item);
            item.price = pricing.basePrice;
            // Keep discountPrice as the discount amount (from request payload)
            // finalPrice is already calculated in resolveProductPricing
            item.totalPrice = pricing.finalPrice * item.quantity;
          }
        } catch (itemError) {
          throw new Error(`Item validation failed: ${itemError.message}`);
        }
      }

      // Variables to store coupon details for order
      let appliedCoupon = null;
      let couponDetails = null;

      // Validate coupon if provided (either coupon ID or couponCode)
      if (createOrderDto?.coupon || createOrderDto?.couponCode) {
        try {
          let coupon;
          
          // Priority: use couponCode if provided, otherwise use coupon ID
          if (createOrderDto.couponCode) {
            // Direct coupon code provided
            coupon = await this.couponService.findByCode(createOrderDto.couponCode);
          } else if (createOrderDto.coupon) {
            // Check if coupon is provided as ID or code
            if (typeof createOrderDto.coupon === 'string' && createOrderDto.coupon.length === 24) {
              // Assume it's an ObjectId
              coupon = await this.couponService.findById(createOrderDto.coupon);
            } else {
              // Assume it's a coupon code
              coupon = await this.couponService.findByCode(createOrderDto.coupon.toString());
            }
          }

          if (!coupon) {
            const couponReference = createOrderDto.couponCode || createOrderDto.coupon;
            console.error(`[Order Service] Coupon not found: ${couponReference}`);
            throw new Error(`Coupon not found: ${couponReference}`);
          }

          console.log(`[Order Service] Coupon found: ${coupon.code}, Type: ${coupon.type}, Active: ${coupon.isActive}`);

          // Validate using the service method
          const promoValidation = await this.couponService.validatePromoCode(
            coupon.code,
            createOrderDto.subtotal,
            createOrderDto.shippingCost,
            undefined, // userId - can be added later
            createOrderDto.shippingAddress?.country?.toString(),
            createOrderDto.shippingAddress?.state?.toString()
          );

          if (!promoValidation.valid) {
            throw new Error(promoValidation.message || 'Coupon is not valid');
          }
          
          // Store coupon reference and details
          appliedCoupon = coupon;
          couponDetails = {
            code: coupon.code,
            discountType: coupon.type,
            value: coupon.value,
            isShippingDiscount: coupon.isShippingDiscount,
            discountAmount: promoValidation.discount || 0,
            shippingDiscountAmount: promoValidation.shippingDiscount || 0
          };
          
          // Apply the validated discounts
          if (coupon.isShippingDiscount) {
            // Apply shipping discount
            createOrderDto.shippingDiscount = promoValidation.shippingDiscount || 0;
            createOrderDto.shippingCost = Math.max(0, createOrderDto.shippingCost - createOrderDto.shippingDiscount);
          } else {
            // Apply regular discount
            createOrderDto.discount = promoValidation.discount || 0;
          }
          
          // Log the applied discount for debugging
          console.log(`[Order Service] Coupon applied: ${coupon.code}, Type: ${coupon.isShippingDiscount ? 'Shipping' : 'Regular'}, Discount: ${coupon.isShippingDiscount ? promoValidation.shippingDiscount : promoValidation.discount}`);
          
        } catch (couponError) {
          const couponReference = createOrderDto.couponCode || createOrderDto.coupon;
          console.error(`[Order Service] Coupon validation failed for ${couponReference}: ${couponError.message}`);
          throw new Error(`Coupon validation failed: ${couponError.message}`);
        }
      }

      // Generate order number
      const orderNumber = `ORD-${Date.now()}`;

      // Recalculate totals to ensure accuracy
      // All items should have totalPrice calculated by now (from validation loop above)
      const calculatedSubtotal = createOrderDto.items.reduce((acc, item) => {
        const itemTotal = item.totalPrice || 0;
        return acc + itemTotal;
      }, 0);
      
      // Use provided values or defaults
      const tax = createOrderDto.tax || 0;
      const shippingCost = createOrderDto.shippingCost || 0;
      const discount = createOrderDto.discount || 0;
      const shippingDiscount = createOrderDto.shippingDiscount || 0;
      
      // Calculate final total
      const calculatedTotal = calculatedSubtotal + tax + shippingCost - discount;
      
      // Update DTO with calculated values (backend is source of truth for calculations)
      createOrderDto.subtotal = Number(calculatedSubtotal.toFixed(2));
      createOrderDto.tax = Number(tax.toFixed(2));
      createOrderDto.shippingCost = Number(shippingCost.toFixed(2));
      createOrderDto.discount = Number(discount.toFixed(2));
      createOrderDto.shippingDiscount = Number(shippingDiscount.toFixed(2));
      createOrderDto.total = Number(calculatedTotal.toFixed(2));
      
      // Recalculate cashPayment.changeDue based on calculated total
      if (createOrderDto.cashPayment) {
        const amountPaid = typeof createOrderDto.cashPayment.amountPaid === 'number' ? createOrderDto.cashPayment.amountPaid : 0;
        const changeDue = amountPaid - calculatedTotal;
        createOrderDto.cashPayment.changeDue = Number(changeDue.toFixed(2));
      }
      
      try {
        // Convert string IDs to ObjectIds for the order model and ensure proper multilingual format
        const orderData = {
          ...createOrderDto,
          items: createOrderDto.items.map(item => ({
            ...item,
            productId: typeof item.productId === 'string' ? new Types.ObjectId(item.productId) : item.productId,
            // Ensure selectedVariants have proper multilingual format
            selectedVariants: item.selectedVariants?.map(variant => ({
              variant: variant.variant,
              value: this.ensureMultilingualValue(variant.value)
            })),
            // Ensure packageItems selectedVariants have proper multilingual format
            packageItems: item.packageItems?.map(packageItem => ({
              ...packageItem,
              selectedVariants: packageItem.selectedVariants?.map(variant => ({
                variant: variant.variant,
                value: this.ensureMultilingualValue(variant.value)
              }))
            }))
          })),
          customerId: createOrderDto.customerId ? (typeof createOrderDto.customerId === 'string' ? new Types.ObjectId(createOrderDto.customerId) : createOrderDto.customerId) : undefined,
          user: createOrderDto.user ? (typeof createOrderDto.user === 'string' ? new Types.ObjectId(createOrderDto.user) : createOrderDto.user) : undefined,
          coupon: appliedCoupon ? appliedCoupon._id : undefined,
          couponDetails: couponDetails,
          orderId: createOrderDto.orderId ? (typeof createOrderDto.orderId === 'string' ? new Types.ObjectId(createOrderDto.orderId) : createOrderDto.orderId) : undefined,
          orderDate: createOrderDto.orderDate,
          orderNumber,
          paymentStatus: this.determinePaymentStatus(createOrderDto),
          orderStatus: createOrderDto.orderStatus || OrderStatus.PENDING,
          cashPayment: createOrderDto.cashPayment ? {
            amountPaid: typeof createOrderDto.cashPayment.amountPaid === 'number' ? createOrderDto.cashPayment.amountPaid : 0,
            changeDue: typeof createOrderDto.cashPayment.changeDue === 'number' ? createOrderDto.cashPayment.changeDue : 0,
            paymentImage: createOrderDto.cashPayment.paymentImage || '',
          } : null,
          isDeposit: !!createOrderDto.isDeposit,
        };
        const order = new this.orderModel(orderData);
        
        // Log before save
        // Save order first
        const savedOrder = await order.save();
        // Update product quantities, soldCount and record sales in inventory
        for (const item of createOrderDto.items) {
          try {
            if (item.itemType === OrderItemType.PACKAGE) {
              // For packages, update package stock, soldCount and individual product stocks
              const packageId = this.extractProductId(item);
              await this.packageService.updateStock(packageId, -item.quantity);
              await this.packageService.incrementSoldCount(packageId, item.quantity);
              
              // Update individual product stocks within the package
              if (item.packageItems && item.packageItems.length > 0) {
                for (const packageItem of item.packageItems) {
                  await this.productService.updateStock(packageItem.productId, -packageItem.quantity);
                  await this.productService.incrementSoldCount(packageItem.productId, packageItem.quantity);
                }
              }
            } else {
              // For products, update stock and soldCount
              const productId = this.extractProductId(item);
              await this.productService.updateStock(productId, -item.quantity);
              await this.productService.incrementSoldCount(productId, item.quantity);
            }
          } catch (stockError) {
            // Don't throw here, just log the error
          }
        }

        // Record sales in inventory system
        try {
          const inventoryItems: any[] = [];
          
          for (const item of createOrderDto.items) {
            if (item.itemType === OrderItemType.PACKAGE) {
              // For packages, record individual products within the package
              if (item.packageItems && item.packageItems.length > 0) {
                for (const packageItem of item.packageItems) {
                  inventoryItems.push({
                    productId: packageItem.productId,
                    quantity: packageItem.quantity,
                    price: item.price, // Use package price
                  });
                }
              }
            } else {
              // For individual products, record normally
              inventoryItems.push({
                productId: this.extractProductId(item),
                quantity: item.quantity,
                price: item.price,
              });
            }
          }
          
          if (inventoryItems.length > 0) {
            await this.inventoryService.recordSale(
              savedOrder._id.toString(),
              inventoryItems
            );
          } else {
          }
        } catch (inventoryError) {
          // Don't throw here, just log the error
        }

        // Record accounting transactions
        try {
          const userId = request?.headers?.authorization ? this.extractUserIdFromRequest(request) : undefined;
          await this.inventoryService.recordAccountingTransaction(
            savedOrder._id.toString(),
            {
              orderNumber: savedOrder.orderNumber,
              total: savedOrder.total,
              items: savedOrder.items,
              paymentMethod: savedOrder.paymentMethod,
              shippingCost: savedOrder.shippingCost,
            },
            userId
          );
        } catch (accountingError) {
          // Don't throw here, just log the error
        }

        // Update coupon usage if applicable
        if (appliedCoupon) {
          try {
            await this.couponService.incrementUsage(appliedCoupon._id.toString());
            console.log(`[Order Service] Coupon usage incremented: ${appliedCoupon.code}`);
          } catch (couponError) {
            console.error(`[Order Service] Failed to increment coupon usage: ${couponError.message}`);
            // Don't throw here, just log the error
          }
        }

        // Update customer statistics
        if (savedOrder.shippingAddress?.phone) {
          try {
            await this.customerStatisticsService.updateCustomerStatistics({
              phone: savedOrder.shippingAddress.phone,
              fullName: savedOrder.shippingAddress.fullName,
              total: savedOrder.subtotal, // Use subtotal instead of total (sales amount)
              state: savedOrder.shippingAddress.state,
              country: savedOrder.shippingAddress.country,
              city: savedOrder.shippingAddress.city,
              address: savedOrder.shippingAddress.address,
            });
          } catch (customerStatsError) {
            // Don't throw here, just log the error
          }
        }

        // Send new order email notification
        try {
          await this.sendOrderEmailNotification(savedOrder);
        } catch (emailError) {
          // Don't throw here, just log the error
        }

        return savedOrder;
      } catch (saveError) {
        throw new Error(`Failed to save order: ${saveError.message}`);
      }
      
    } catch (error) {
      throw error; // Re-throw to be caught by controller
    }
  }

  async findAll(query: any): Promise<{ orders: OrderDocument[]; pagination: any }> {
    const { page = 1, limit = 10, sort = 'createdAt', order = 'desc', ...filters } = query;
    const skip = (page - 1) * limit;
    const total = await this.orderModel.countDocuments(filters).exec();
    const orders = await this.orderModel
      .find(filters)
      .populate('user', 'name email')
      .populate({ path: 'items.itemId' })
      .populate({ path: 'items.packageItems.productId' })
      .sort({ createdAt: -1 })
      .skip(skip)
      .limit(limit)
      .exec();
    return {
      orders,
      pagination: {
        page,
        limit,
        total,
        pages: Math.ceil(total / limit),
      },
    };
  }

  async findOne(id: string): Promise<OrderDocument> {
    return this.orderModel
      .findById(id)
      .populate('user', 'name email')
      .populate({
        path: 'items.itemId',
        select: 'name price images'
      })
      .populate({
        path: 'items.packageItems.productId',
        select: 'name price images variants'
      })
      .exec();
  }

  async findByOrderNumber(orderNumber: string): Promise<any> {
    const order = await this.orderModel
      .findOne({ orderNumber })
      .populate('user', 'name email')
      .populate({
        path: 'items.itemId',
      })
      .populate({
        path: 'items.packageItems.productId',
      })
      .populate({
        path: 'shippingAddress.country',
        select: 'name code defaultShippingCost'
      })
      .populate({
        path: 'shippingAddress.state',
        select: 'name code shippingCost countryId'
      })
      .exec();

    if (!order) {
      throw new Error(`Order with number ${orderNumber} not found`);
    }

    // Transform shippingAddress to ensure country and state are objects
    const shippingAddress = order.shippingAddress ? {
      fullName: order.shippingAddress.fullName,
      address: order.shippingAddress.address,
      city: order.shippingAddress.city,
      phone: order.shippingAddress.phone,
      country: order.shippingAddress.country || null,
      state: order.shippingAddress.state || null,
    } : {};

    // Transform data to match frontend model
    return {
      id: order._id,
      orderNumber: order.orderNumber,
      status: order.orderStatus,
      date: order.createdAt,
      total: order.total,
      paymentMethod: order.paymentMethod,
      paymentStatus: order.paymentStatus,
      items: order.items,
      subtotal: order.subtotal || 0,
      shipping: order.shippingCost || 0,
      tax: order.tax || 0,
      shippingAddress: shippingAddress,
      billingAddress: null,
      trackingNumber: null,
      notes: order.notes,
      userId: order.user || null,
      isDeposit: order.isDeposit || false,
      amountPaid: order.cashPayment?.amountPaid || 0,
      // Keep original fields for backward compatibility
      _id: order._id,
      orderStatus: order.orderStatus,
      createdAt: order.createdAt,
      updatedAt: order.updatedAt
    };
  }

  async update(id: string, updateOrderDto: Partial<CreateOrderDto>): Promise<OrderDocument> {
    try {
      const order = await this.findOne(id);
      if (!order) {
        throw new Error('Order not found');
      }

      // Prepare update object
      const updateData: any = {};

      // Handle items update - recalculate prices like in create method
      if (updateOrderDto.items && Array.isArray(updateOrderDto.items)) {
        
        // Validate and recalculate prices for each item
        for (const item of updateOrderDto.items) {
          
          try {
            if (item.itemType === OrderItemType.PACKAGE) {
              // Handle package validation and pricing
              const packageId = this.extractProductId(item);
              const packageData = await this.packageService.findOne(packageId);
              if (!packageData) {
                throw new Error(`Package with ID ${packageId} not found`);
              }
              
              // Validate package items and their variants
              if (item.packageItems && item.packageItems.length > 0) {
                await this.validatePackageItems(packageData, item.packageItems);
                
                // Transform selectedVariants to ensure proper format
                item.packageItems = item.packageItems.map(packageItem => ({
                  ...packageItem,
                  selectedVariants: packageItem.selectedVariants?.map(variant => ({
                    variant: variant.variant,
                    value: this.ensureMultilingualValue(variant.value),
                    image: variant.image,
                  }))
                }));
              }
              
              // Set price if not provided
              if (!item.price) {
                const basePackagePrice = packageData.price ?? 0;
                const discountedPackagePrice =
                  packageData.discountPrice != null && packageData.discountPrice < basePackagePrice
                    ? packageData.discountPrice
                    : undefined;

                item.price = basePackagePrice;
                if (discountedPackagePrice != null) {
                  if (!item.discountPrice) {
                    item.discountPrice = basePackagePrice - discountedPackagePrice;
                  }
                }
              }

              // Calculate final price: basePrice - discountPrice (treat discountPrice as discount amount)
              const finalPackagePrice =
                item.discountPrice != null && item.discountPrice >= 0
                  ? Math.max(0, item.price - item.discountPrice)
                  : item.price;

              item.totalPrice = finalPackagePrice * item.quantity;
            } else {
              // Handle product validation and pricing
              const productId = this.extractProductId(item);
              const product: ProductDocument = await this.productService.findOne(productId);
              if (!product) {
                throw new Error(`Product with ID ${productId} not found`);
              }
              
              const pricing = this.resolveProductPricing(product, item);
              item.price = pricing.basePrice;
              item.totalPrice = pricing.finalPrice * item.quantity;
            }
          } catch (itemError) {
            throw new Error(`Item validation failed: ${itemError.message}`);
          }
        }
        
        // Convert string IDs to ObjectIds and ensure proper format
        updateData.items = updateOrderDto.items.map(item => ({
          ...item,
          itemId: item.itemId ? (typeof item.itemId === 'string' ? new Types.ObjectId(item.itemId) : item.itemId) : undefined,
          productId: item.productId ? (typeof item.productId === 'string' ? new Types.ObjectId(item.productId) : item.productId) : undefined,
          // Ensure selectedVariants have proper multilingual format
          selectedVariants: item.selectedVariants?.map(variant => ({
            variant: variant.variant,
            value: this.ensureMultilingualValue(variant.value)
          })),
          // Ensure packageItems selectedVariants have proper multilingual format
          packageItems: item.packageItems?.map(packageItem => ({
            ...packageItem,
            productId: packageItem.productId ? (typeof packageItem.productId === 'string' ? new Types.ObjectId(packageItem.productId) : packageItem.productId) : undefined,
            selectedVariants: packageItem.selectedVariants?.map(variant => ({
              variant: variant.variant,
              value: this.ensureMultilingualValue(variant.value)
            }))
          }))
        }));
        
        // Recalculate subtotal from updated items
        updateData.subtotal = updateData.items.reduce((acc: number, item: any) => acc + (item.totalPrice || 0), 0) || 0;
      }

      // Handle shipping address update
      if (updateOrderDto.shippingAddress) {
        // Convert country and state strings to ObjectIds if needed
        const shippingAddress: any = { ...updateOrderDto.shippingAddress };
        if (shippingAddress.country && typeof shippingAddress.country === 'string') {
          shippingAddress.country = new Types.ObjectId(shippingAddress.country);
        }
        if (shippingAddress.state && typeof shippingAddress.state === 'string') {
          shippingAddress.state = new Types.ObjectId(shippingAddress.state);
        }
        updateData.shippingAddress = shippingAddress;
      }
      // Handle payment status update
      if (updateOrderDto.paymentStatus) {
        updateData.paymentStatus = updateOrderDto.paymentStatus;
      }

      // Handle order status update
      if (updateOrderDto.orderStatus) {
        updateData.orderStatus = updateOrderDto.orderStatus;
      }

      // Handle payment method update
      if (updateOrderDto.paymentMethod) {
        updateData.paymentMethod = updateOrderDto.paymentMethod;
      }
      // Handle cash payment update
      if (updateOrderDto.cashPayment) {
        const cashPaymentObj = updateOrderDto.cashPayment as any;
        // Check if amountPaid is provided (can be 0 or positive number)
        if (cashPaymentObj.amountPaid !== undefined && cashPaymentObj.amountPaid !== null) {
          const cashAmount = typeof cashPaymentObj.amountPaid === 'number' ? cashPaymentObj.amountPaid : 0;
          const totalAmount = updateOrderDto.total || order.total;
          
          // Use changeDue from request if provided, otherwise calculate it
          const changeDue = cashPaymentObj.changeDue !== undefined && cashPaymentObj.changeDue !== null
            ? (typeof cashPaymentObj.changeDue === 'number' ? cashPaymentObj.changeDue : 0)
            : (cashAmount - totalAmount);
          
          
          // Build cashPayment object with all provided values
          updateData.cashPayment = {
            amountPaid: cashAmount,
            changeDue: changeDue,
            paymentImage: cashPaymentObj.paymentImage !== undefined ? cashPaymentObj.paymentImage : (order.cashPayment?.paymentImage || ''),
          };
          // Update payment status based on amount paid
          if (cashAmount >= totalAmount) {
            updateData.paymentStatus = PaymentStatus.PAID;
          } else if (cashAmount > 0) {
            updateData.paymentStatus = PaymentStatus.PENDING;
          }
        } else if (cashPaymentObj.changeDue !== undefined || cashPaymentObj.paymentImage !== undefined) {
          // If only changeDue or paymentImage is being updated, preserve existing amountPaid
          updateData.cashPayment = {
            ...order.cashPayment,
            amountPaid: order.cashPayment?.amountPaid || 0,
            changeDue: cashPaymentObj.changeDue !== undefined ? (typeof cashPaymentObj.changeDue === 'number' ? cashPaymentObj.changeDue : order.cashPayment?.changeDue || 0) : order.cashPayment?.changeDue || 0,
            paymentImage: cashPaymentObj.paymentImage !== undefined ? cashPaymentObj.paymentImage : (order.cashPayment?.paymentImage || ''),
          };
        }
      }



      // Handle delivery date update
      if (updateOrderDto.deliveryDate) {
        updateData.deliveredAt = updateOrderDto.deliveryDate;
      }

      // Handle notes update
      if (updateOrderDto.notes !== undefined) {
        updateData.notes = updateOrderDto.notes;
      }

      // Handle financial fields update
      // Use recalculated subtotal if items were updated, otherwise use provided value or existing value
      if (updateData.subtotal === undefined) {
        if (updateOrderDto.subtotal !== undefined) {
          updateData.subtotal = updateOrderDto.subtotal;
        } else {
          updateData.subtotal = order.subtotal || 0;
        }
      }

      if (updateOrderDto.tax !== undefined) {
        updateData.tax = updateOrderDto.tax;
      } else if (updateData.tax === undefined) {
        updateData.tax = order.tax || 0;
      }

      if (updateOrderDto.shippingCost !== undefined) {
        updateData.shippingCost = updateOrderDto.shippingCost;
      } else if (updateData.shippingCost === undefined) {
        updateData.shippingCost = order.shippingCost || 0;
      }

      if (updateOrderDto.discount !== undefined) {
        updateData.discount = updateOrderDto.discount;
      } else if (updateData.discount === undefined) {
        updateData.discount = order.discount || 0;
      }

      // Recalculate total: subtotal + tax + shippingCost - discount
      const subtotal = updateData.subtotal ?? order.subtotal ?? 0;
      const tax = updateData.tax ?? order.tax ?? 0;
      const shippingCost = updateData.shippingCost ?? order.shippingCost ?? 0;
      const discount = updateData.discount ?? order.discount ?? 0;
      updateData.total = subtotal + tax + shippingCost - discount;

      // Handle coupon update
      if (updateOrderDto.coupon !== undefined) {
        updateData.coupon = typeof updateOrderDto.coupon === 'string' ? new Types.ObjectId(updateOrderDto.coupon) : updateOrderDto.coupon;
      }

      // Update the order
      const updatedOrder = await this.orderModel.findByIdAndUpdate(
        id, 
        updateData, 
        { new: true, runValidators: true }
      ).exec();

      if (!updatedOrder) {
        throw new Error('Failed to update order');
      }

      return updatedOrder;
    } catch (error) {
      throw new Error(`Failed to update order: ${error.message}`);
    }
  }

  private determinePaymentStatus(createOrderDto: CreateOrderDto): PaymentStatus {
    // If paymentStatus is explicitly provided, use it
    if (createOrderDto.paymentStatus) {
      return createOrderDto.paymentStatus;
    }
    
    // Determine payment status based on payment method and cash payment
    if (createOrderDto.paymentMethod === PaymentMethod.CASH && createOrderDto.cashPayment) {
      const amountPaid = createOrderDto.cashPayment.amountPaid || 0;
      const total = createOrderDto.total || 0;
      
      // If amount paid is greater than or equal to total, payment is complete
      if (amountPaid >= total) {
        return PaymentStatus.PAID;
      } else if (amountPaid > 0) {
        // Partial payment
        return PaymentStatus.PENDING;
      }
    }
    
    // For other payment methods or no cash payment, default to pending
    return PaymentStatus.PENDING;
  }

  private resolveProductPricing(product: ProductDocument, item: any): { basePrice: number; finalPrice: number } {
    let basePrice: number;

    // If useVariantPrice is true, get price from variant
    // If useVariantPrice is false, use product.price
    if (product.useVariantPrice) {
      const variantPrice = this.resolveProductVariantPrice(product, item?.selectedVariants);
      // If price is provided in item, use it (from frontend calculation)
      // Otherwise, use variant price or fallback to product price
      basePrice =
        item?.price != null
          ? item.price
          : variantPrice != null
            ? variantPrice
            : product.price ?? 0;
    } else {
      // useVariantPrice is false, use product.price
      basePrice = item?.price != null ? item.price : product.price ?? 0;
    }

    let finalPrice: number;
    // If discountPrice is provided in the item, treat it as a discount amount (not final price)
    // Calculate final price as: basePrice - discountPrice
    if (item?.discountPrice != null && item.discountPrice >= 0) {
      finalPrice = Math.max(0, basePrice - item.discountPrice);
    } else if (product.discountPrice != null && product.discountPrice >= 0 && product.discountPrice < basePrice) {
      // Product discountPrice is the final sale price, not discount amount
      finalPrice = product.discountPrice;
    } else {
      finalPrice = basePrice;
    }

    return { basePrice, finalPrice };
  }

  private resolveProductVariantPrice(product: ProductDocument, selectedVariants?: any[]): number | undefined {
    if (!product?.variants?.length) {
      return undefined;
    }

    // If no variants selected, return the first variant price or minimum price
    if (!selectedVariants?.length) {
      const variantPrices = product.variants
        .map(variant => variant.price)
        .filter((price): price is number => price != null);
      if (variantPrices.length) {
        // Return first variant price if available, otherwise minimum
        return product.variants[0]?.price ?? Math.min(...variantPrices);
      }
      return undefined;
    }

    // Find matching variant based on selected attributes
    const matchingVariant = product.variants.find(variant =>
      this.variantMatchesSelection(variant, selectedVariants)
    );

    // If matching variant found, return its price
    if (matchingVariant?.price != null) {
      return matchingVariant.price;
    }

    // If no matching variant, return minimum variant price as fallback
    const variantPrices = product.variants
      .map(variant => variant.price)
      .filter((price): price is number => price != null);
    
    return variantPrices.length ? Math.min(...variantPrices) : undefined;
  }

  private variantMatchesSelection(variant: any, selectedVariants: any[]): boolean {
    if (!variant?.attributes?.length) {
      return false;
    }

    return variant.attributes.every((attribute: any) =>
      selectedVariants.some(selected => {
        if (selected.variant !== attribute.variant) {
          return false;
        }
        const selectedValue = this.normalizeVariantValue(selected.value);
        const attributeValue = this.normalizeVariantValue(attribute.value);
        return selectedValue.en === attributeValue.en && selectedValue.ar === attributeValue.ar;
      })
    );
  }

  private normalizeVariantValue(value: any): { en: string; ar: string } {
    if (!value) {
      return { en: '', ar: '' };
    }

    if (typeof value === 'string') {
      const normalized = value.toString().toLowerCase().trim();
      return { en: normalized, ar: normalized };
    }

    return {
      en: (value.en || value.ar || '').toString().toLowerCase().trim(),
      ar: (value.ar || value.en || '').toString().toLowerCase().trim()
    };
  }

  async remove(id: string): Promise<void> {
    // Use findById without populate to avoid issues with deleted packages
    const order = await this.orderModel.findById(id).exec();
    if (!order) {
      throw new Error('Order not found');
    }

    // Return stock to products and packages
    for (const item of order.items) {
      try {
        if (item.itemType === OrderItemType.PACKAGE) {
          // For packages, return stock to package and individual products
          const packageId = this.extractProductId(item);
          
          // Return stock to package
          try {
            await this.packageService.updateStock(packageId, item.quantity);
          } catch (packageError) {
          }
          
          // Return stock to individual products within the package
          if (item.packageItems && item.packageItems.length > 0) {
            for (const packageItem of item.packageItems) {
              try {
                await this.productService.updateStockOnRemove(packageItem.productId.toString(), packageItem.quantity);
              } catch (productError) {
              }
            }
          }
        } else {
          // For regular products, return stock normally
          const productId = this.extractProductId(item);
          try {
            await this.productService.updateStockOnRemove(productId, item.quantity);
          } catch (productError) {
          }
        }
      } catch (itemError) {
        // Continue with next item instead of failing
      }
    }
    
    // Decrease coupon usage if applicable
    if (order.coupon) {
      try {
        const couponId = this.extractCouponId(order.coupon);
        await this.couponService.decrementUsage(couponId);
      } catch (couponError) {
      }
    }

    await this.orderModel.findByIdAndDelete(id).exec();
  }

  /**
   * Helper method to safely extract product ID from item
   * Handles both populated objects and direct ObjectId/string values
   */
  private extractProductId(item: any): string {
    // For packages, use itemId instead of productId
    const id = item.itemId || item.productId;
    
    if (!id) {
      throw new Error('Item must have either itemId (for packages) or productId (for products)');
    }
    
    if (typeof id === 'object' && id !== null) {
      // If it's a populated object, get the _id
      return id._id?.toString() || id.toString();
    } else {
      // If it's already a string or ObjectId, convert to string
      return id.toString();
    }
  }

  private async validatePackageItems(packageData: any, packageItems: any[]): Promise<void> {
    for (const packageItem of packageItems) {
      // Find the corresponding item in the package
      const packageItemData = packageData.items.find((item: any) => 
        item.productId._id.toString() === packageItem.productId
      );
      
      if (!packageItemData) {
        throw new Error(`Product ${packageItem.productId} not found in package ${packageData._id}`);
      }
      
      // Validate product exists and has stock
      const product = await this.productService.findOne(packageItem.productId);
      if (!product) {
        throw new Error(`Product with ID ${packageItem.productId} not found`);
      }
      
      // Check if product has sufficient stock
      if (product.stock < packageItem.quantity) {
        throw new Error(`Insufficient stock for product ${product.name}. Available: ${product.stock}, Requested: ${packageItem.quantity}`);
      }
      
      // Validate required variants
      if (packageItemData.requiredVariantAttributes && packageItemData.requiredVariantAttributes.length > 0) {
        const selectedVariants = packageItem.selectedVariants || [];
        const requiredVariants = packageItemData.requiredVariantAttributes;
        
        for (const requiredVariant of requiredVariants) {
          const selectedVariant = selectedVariants.find((v: any) => v.variant === requiredVariant.variant);
          if (!selectedVariant) {
            throw new Error(`Required variant '${requiredVariant.variant}' not selected for product ${product.name}`);
          }
          
          // Ensure selected variant value is in proper format
          selectedVariant.value = this.ensureMultilingualValue(selectedVariant.value);
        }
      }
    }
  }

  /**
   * Helper method to safely extract coupon ID
   * Handles both populated objects and direct ObjectId/string values
   */
  private extractCouponId(coupon: any): string {
    if (typeof coupon === 'object' && coupon !== null) {
      // If it's a populated object, get the _id
      return coupon._id?.toString() || coupon.toString();
    } else {
      // If it's already a string or ObjectId, convert to string
      return coupon.toString();
    }
  }

  /**
   * Helper method to ensure value is in proper multilingual format
   */
  private ensureMultilingualValue(value: any): { en: string; ar: string } {
    
    if (typeof value === 'string') {
      return { en: value, ar: value };
    }
    
    if (value && typeof value === 'object') {
      // If it's already a multilingual object
      if (value.en !== undefined || value.ar !== undefined) {
        const result = {
          en: value.en || value.ar || '',
          ar: value.ar || value.en || ''
        };
        return result;
      }
    }
    
    // Fallback to empty multilingual object
    return { en: '', ar: '' };
  }

  async processCashPayment(orderId: string, amountPaid: number): Promise<OrderDocument> {
    const order = await this.findOne(orderId);
    if (!order) {
      throw new Error('Order not found');
    }

    if (order.paymentMethod !== 'cash') {
      throw new Error('Invalid payment method');
    }

    if (order.paymentStatus !== PaymentStatus.PENDING) {
      throw new Error('Payment already processed');
    }

    const changeDue = amountPaid - order.total;
    if (changeDue >= 0) {
      order.paymentStatus = PaymentStatus.PAID;
      order.cashPayment = {
        amountPaid,
        changeDue,
      };
      order.paymentMethod = PaymentMethod.CASH;
    } else {
      throw new Error('Insufficient payment amount');
    }

    return await order.save();
  }

  /**
   * Send email notification for new order
   */
  private async sendOrderEmailNotification(order: OrderDocument): Promise<void> {
    try {
      // Get product/package names for email
      const emailItems = await Promise.all(
        order.items.map(async (item) => {
          let itemName = 'Unknown Item';
          
          try {
            if (item.itemType === OrderItemType.PACKAGE) {
              const pkg = await this.packageService.findOne(item.itemId.toString());
              itemName = pkg?.name?.en || pkg?.name?.ar || 'Package';
            } else {
              const product = await this.productService.findOne(item.itemId.toString());
              itemName = product?.name?.en || product?.name?.ar || 'Product';
            }
          } catch (error) {
          }

          return {
            name: itemName,
            quantity: item.quantity,
            price: item.price || 0,
            totalPrice: item.totalPrice || (item.price || 0) * item.quantity,
          };
        })
      );

      // Get amountPaid from cashPayment if exists
      const amountPaid = order.cashPayment?.amountPaid || undefined;

      await this.emailService.sendNewOrderEmail({
        orderId: order._id.toString(),
        orderNumber: order.orderNumber,
        customerName: order.shippingAddress?.fullName || 'Guest Customer',
        phone: order.shippingAddress?.phone || 'N/A',
        subtotal: order.subtotal,
        shippingCost: order.shippingCost || 0,
        tax: order.tax || 0,
        discount: order.discount || 0,
        total: order.total,
        amountPaid: amountPaid,
        items: emailItems,
      });
    } catch (error) {
      // Don't throw to avoid breaking order creation
    }
  }
}
